import React, { useEffect, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import axios from "axios";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import { useEngagementTheme, engagementPageSx, EngagementCard, engagementButtonSx } from "../components/engagementTheme";

const API = `${process.env.REACT_APP_API_HOST_URL}/ops/v1/engagement/settings`;


const WEIGHT_DEFS = [
  ["usage", "Usage pattern", "Scans in 14 days against the clinic baseline"],
  ["contact", "Human contact", "Days since a real conversation against the tier limit"],
  ["promises", "Promises kept", "Minus 5 per overdue promise"],
  ["nps", "Feedback and NPS", "Latest survey score in 180 days"],
  ["issues", "Hardware and issues", "Open tickets, recent complaints closed in 30 days"],
];

const LIMIT_DEFS = [
  ["high", "High-touch contact limit, days"],
  ["standard", "Standard contact limit, days"],
  ["self", "Self-serve contact limit, days"],
  ["cap", "Daily task cap per officer"],
  ["grace", "Grace period after end date, days"],
];

const EngagementSettings = () => {
  const t = useEngagementTheme();
  const [weights, setWeights] = useState(null);
  const [limits, setLimits] = useState(null);
  const [history, setHistory] = useState([]);
  const [version, setVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const Toast = useToast();
  const auth = localStorage.getItem("token");
  const authHeaders = { headers: { Authorization: `Bearer ${auth}` } };

  const load = () => {
    setLoading(true);
    return axios
      .get(API, authHeaders)
      .then((res) => {
        const { current, history: h } = res.data.data;
        setWeights(current.weights);
        setLimits(current.limits);
        setVersion(current.version);
        setHistory(h || []);
      })
      .catch(() => Toast.error("Could not load settings."))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading || !weights || !limits) {
    return (
      <Box sx={{ ...engagementPageSx(t), position: "relative", minHeight: 300 }}>
        <PageLoader />
      </Box>
    );
  }

  const total = WEIGHT_DEFS.reduce((sum, [key]) => sum + Number(weights[key] || 0), 0);
  const badTotal = total !== 100;

  const setWeight = (key, value) => {
    const n = parseInt(value, 10);
    setWeights((prev) => ({ ...prev, [key]: isNaN(n) ? 0 : n }));
  };
  const setLimit = (key, value) => {
    const n = parseInt(value, 10);
    setLimits((prev) => ({ ...prev, [key]: isNaN(n) ? 0 : n }));
  };

  const save = async () => {
    if (badTotal) return;
    setSaving(true);
    try {
      const cleanWeights = Object.fromEntries(WEIGHT_DEFS.map(([key]) => [key, Number(weights[key] || 0)]));
      await axios.post(API, { weights: cleanWeights, limits }, authHeaders);
      Toast.success(`Saved as version ${version + 1}. Takes effect immediately.`);
      await load();
    } catch (error) {
      Toast.error(error.response?.data?.message || "Could not save settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={engagementPageSx(t)}>
      <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-end", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography sx={{ fontSize: 30, fontWeight: 700, letterSpacing: "-0.015em" }}>Settings and rules</Typography>
          <Typography sx={{ fontSize: 15, color: t.textMuted }}>
            Health score weights and Action Centre thresholds. Every save creates a new version and takes effect immediately.
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          disabled={saving || badTotal}
          onClick={save}
          sx={{ ...engagementButtonSx(t, "solid"), opacity: saving || badTotal ? 0.6 : 1 }}
        >
          {saving ? "Saving…" : `Save as version ${version + 1}`}
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "20px", alignItems: "flex-start" }}>
        <EngagementCard t={t} sx={{ flex: "999 1 520px", minWidth: 0 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "baseline", gap: "8px" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Renewal health weights</Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: badTotal ? t.tone.crit.color : t.tone.ok.color }}>
              Total {total} of 100
            </Typography>
          </Box>
          {WEIGHT_DEFS.map(([key, label, rule]) => (
            <Box key={key} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "14px", paddingTop: "10px", borderTop: `1px solid ${t.borderMuted}` }}>
              <Box sx={{ flex: "1 1 260px", minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
                <Typography sx={{ fontSize: 15, fontWeight: 600 }}>{label}</Typography>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>{rule}</Typography>
              </Box>
              <TextField
                size="small"
                type="number"
                inputProps={{ min: 0, max: 60 }}
                value={weights[key]}
                onChange={(e) => setWeight(key, e.target.value)}
                sx={{ bgcolor: t.inputBg, width: 96 }}
              />
            </Box>
          ))}
          {badTotal && (
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.tone.crit.color }}>
              Weights must add up to 100 before you can save.
            </Typography>
          )}
        </EngagementCard>

        <Box sx={{ flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: "16px" }}>
          <EngagementCard t={t}>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Thresholds</Typography>
            {LIMIT_DEFS.map(([key, label]) => (
              <Box key={key} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                <Typography sx={{ fontSize: 14 }}>{label}</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={limits[key]}
                  onChange={(e) => setLimit(key, e.target.value)}
                  sx={{ bgcolor: t.inputBg, width: 88 }}
                />
              </Box>
            ))}
          </EngagementCard>

          <EngagementCard t={t}>
            <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Version history</Typography>
            {history.length === 0 && (
              <Typography sx={{ fontSize: 14, color: t.textMuted }}>No saved versions yet — these are the real defaults every part of the app already uses.</Typography>
            )}
            {history.map((h) => (
              <Box key={h.version} sx={{ display: "flex", flexDirection: "column", gap: "2px", paddingTop: "8px", borderTop: `1px solid ${t.borderMuted}` }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  Version {h.version}{h.version === version ? ", current" : ""}
                </Typography>
                <Typography sx={{ fontSize: 13, color: t.textMuted }}>
                  {new Date(h.createdAt).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  {h.changedBy?.name ? `, ${h.changedBy.name}` : ""}
                </Typography>
              </Box>
            ))}
          </EngagementCard>
        </Box>
      </Box>
    </Box>
  );
};

export default EngagementSettings;
